/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react'
import './text-effects.css'

export const TextEffectTyping = ({
	children,
	speed = 50,
	cursor = true,
	triggerOn = 'viewport', // 'viewport' | 'hover'
	hoverTarget = 'parent', // 'parent' | 'self'
	style = {},
	className = ''
}) => {
	const [visibleLength, setVisibleLength] = useState(0)
	const [isActive, setIsActive] = useState(false)
	const [done, setDone] = useState(false)
	const [triggerCount, setTriggerCount] = useState(0)
	const hasPlayedRef = useRef(false)
	const containerRef = useRef(null)

	const extractText = (nodes) => {
		let text = ''
		React.Children.forEach(nodes, (child) => {
			if (typeof child === 'string') text += child
			else if (child?.props?.children)
				text += extractText(child.props.children)
		})
		return text
	}
	const fullText = extractText(children)

	// Viewport: run once
	useEffect(() => {
		if (triggerOn !== 'viewport' || !containerRef.current) return
		const el = containerRef.current
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry.isIntersecting && !hasPlayedRef.current) {
					hasPlayedRef.current = true
					setDone(false)
					setVisibleLength(0)
					setIsActive(true)
					setTriggerCount((p) => p + 1)
					observer.unobserve(el)
				}
			},
			{ threshold: 0.2 }
		)
		observer.observe(el)
		return () => observer.disconnect()
	}, [triggerOn])

	// Hover: replay every time
	useEffect(() => {
		if (triggerOn !== 'hover' || !containerRef.current) return
		const el =
			hoverTarget === 'parent'
				? containerRef.current.parentElement
				: containerRef.current
		if (!el) return

		const onEnter = () => {
			setDone(false)
			setVisibleLength(0)
			setIsActive(true)
			setTriggerCount((p) => p + 1)
		}
		const onLeave = () => {
			setIsActive(false)
			setDone(false)
			setVisibleLength(0)
		}

		el.addEventListener('mouseenter', onEnter)
		el.addEventListener('mouseleave', onLeave)
		return () => {
			el.removeEventListener('mouseenter', onEnter)
			el.removeEventListener('mouseleave', onLeave)
		}
	}, [triggerOn, hoverTarget])

	// Typing animation (no post-finish delay that causes a blank state)
	useEffect(() => {
		if (!isActive || !fullText) return
		let i = 0
		const interval = setInterval(() => {
			i++
			setVisibleLength(i)
			if (i >= fullText.length) {
				clearInterval(interval)
				setDone(true) // mark done immediately
				setIsActive(false) // safe now; render won’t fall through to null
			}
		}, speed)
		return () => clearInterval(interval)
	}, [triggerCount, isActive, fullText, speed])

	// Render clipped text preserving nested structure
	const renderClipped = (nodes, remaining) => {
		let used = 0
		return React.Children.map(nodes, (child) => {
			if (typeof child === 'string') {
				const part = child.slice(0, Math.max(0, remaining - used))
				used += child.length
				return part
			}
			if (child?.props?.children) {
				const textInChild = extractText(child.props.children)
				const lengthInChild = textInChild.length
				const cloned = React.cloneElement(child, {
					children: renderClipped(
						child.props.children,
						remaining - used
					)
				})
				used += lengthInChild
				return cloned
			}
			return child
		})
	}

	const isTyping = isActive && !done

	return (
		<span
			ref={containerRef}
			className={`we-text-typing ${className}`}
			style={{ whiteSpace: 'pre', ...style }}
		>
			{done
				? children // show full text after finish
				: isTyping
				? renderClipped(children, visibleLength) // typing phase
				: null}{' '}
			{cursor && isTyping && (
				<span className="we-text-typing-cursor">|</span>
			)}
		</span>
	)
}
